import React from 'react';

import {
  Search as SearchIcon,
  Storage as DatabaseIcon,
  Business as BusinessIcon,
  Recommend as RecommendIcon,
  Assignment as LogsIcon,
} from '@mui/icons-material';
import { Box, Typography, Chip, Paper, Link } from '@mui/material';

import config from 'config';
import MessageWrapper from 'pages/Analysis/components/MessageWrapper';
import type { FileSearchToolArguments, RequestRecommendationToolArguments } from 'services/types/ticket';

interface Props {
  toolName: string;
  args: string;
}

const SimpleToolComponent: React.FC<Props> = ({ toolName, args }) => {
  const getToolInfo = (name: string) => {
    switch (name) {
      case 'fileSearch':
        return {
          caption: 'Búsqueda de archivos',
          icon: <SearchIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />,
          description: 'Búsqueda difusa de archivos por nombre o ruta',
        };
      case 'getDatabaseSchema':
        return {
          caption: 'Esquema de base de datos',
          icon: <DatabaseIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />,
          description: 'Cargando estructura de PostgreSQL',
        };
      case 'getBusinessUnitDetails':
        return {
          caption: 'Detalles de unidad de negocio',
          icon: <BusinessIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />,
          description: 'Consultando información de la unidad de negocio',
        };
      case 'requestRecommendation':
        return {
          caption: 'Recomendación de request al backend',
          icon: <RecommendIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />,
          description: 'Solicitando recomendación del sistema',
        };
      case 'readLogs':
        return {
          caption: 'Leer logs del sistema',
          icon: <LogsIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />,
          description: 'Consultando logs de Google Cloud Logging',
        };
      default:
        return {
          caption: name,
          icon: <SearchIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />,
          description: 'Ejecutando herramienta',
        };
    }
  };

  let parsedArgs;
  try {
    parsedArgs = JSON.parse(args) as Record<string, unknown>;
  } catch (error) {
    return (
      <MessageWrapper caption="Herramienta" align="left">
        <Typography color="error">Error al parsear argumentos: {args}</Typography>
      </MessageWrapper>
    );
  }

  const toolInfo = getToolInfo(toolName);

  // Special handling for fileSearch to show query prominently
  if (toolName === 'fileSearch') {
    const fileSearchArgs = parsedArgs as unknown as FileSearchToolArguments;
    return (
      <MessageWrapper caption={toolInfo.caption} align="left">
        <Box>
          <Box mb={2}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {toolInfo.icon}
              Buscando archivos:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                fontWeight: 'bold',
              }}
            >
              {fileSearchArgs.query}
            </Paper>
          </Box>

          {fileSearchArgs.maxResults && (
            <Box mb={1}>
              <Chip
                label={`Máx. ${fileSearchArgs.maxResults} resultados`}
                size="small"
                color="primary"
                sx={{ fontSize: '0.75rem' }}
              />
            </Box>
          )}
        </Box>
      </MessageWrapper>
    );
  }

  // Special handling for getDatabaseSchema - no parameters
  if (toolName === 'getDatabaseSchema') {
    return (
      <MessageWrapper caption={toolInfo.caption} align="left">
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, verticalAlign: 'middle' }}>
            {toolInfo.icon}
            {toolInfo.description}...
          </Typography>
        </Box>
      </MessageWrapper>
    );
  }

  if (toolName === 'requestRecommendation') {
    const { url, method, body, reason } = parsedArgs as unknown as RequestRecommendationToolArguments;
    const fullUrl = `${config.apiUrl}/urls_form?url=${url}&method=${method}&body=${JSON.stringify(body)}`;
    return (
      <MessageWrapper caption={toolInfo.caption} align="left">
        <Box>
          <Typography variant="body2">
            {method} {url}
          </Typography>
          <Typography variant="body2">{JSON.stringify(body)}</Typography>
          <Link href={fullUrl} target="_blank">
            Ejecutar
          </Link>
          <Typography variant="body2">{reason}</Typography>
        </Box>
      </MessageWrapper>
    );
  }

  // Default handling for other tools
  return (
    <MessageWrapper caption={toolInfo.caption} align="left">
      <Box>
        <Box
          sx={{
            fontFamily: 'monospace',
            bgcolor: 'grey.100',
            p: 1.5,
            borderRadius: 1,
            fontSize: '0.875rem',
            maxHeight: '120px',
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          {JSON.stringify(parsedArgs, null, 2)}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {toolInfo.description}
        </Typography>
      </Box>
    </MessageWrapper>
  );
};

export default SimpleToolComponent;
